import type { TFunction } from 'i18next';

import { CLASSES_SEM_COR, classesDaCorMarca, IClassesCorMarca } from '@/domain/cadastros/helpers/paletaMarcas';
import { CelulaTipo } from '@/domain/cadastros/interfaces/cadastros';
import { MapaDiaSemExpediente, MapaMarca } from '@/domain/mapa/interfaces/mapa';
import { FarolCor } from '@/domain/ops/interfaces/ops';

/** Wide enough for "8 pessoas · 120 h de carga" on one line; a longer one truncates with a tooltip. */
export const RAIA_HEADER_W = 184;
/** Three lines in the lane header: the cell name, the load, and the capacity it runs against. */
export const RAIA_HEIGHT = 64;

/** Days in a week and a day in milliseconds: the week arithmetic reads from here. */
const DIAS_SEMANA = 7;
const MS_POR_DIA = 24 * 60 * 60 * 1000;

/**
 * Floor for the band width so the shortest OP stays clickable. Bands are never
 * narrowed to tell one OP from the next: on a timeline white means "nothing
 * scheduled", so a channel between two OPs running back to back would read as idle
 * days on a cell that never stops. Consecutive bands touch and the farol outline is
 * what separates them.
 */
export const FAIXA_MIN_W = 4;

/**
 * The grey of a day with no work, behind the bands and under the date ruler. One class
 * for the stripe and the legend square, so the legend can never drift from what the
 * timeline paints. `border-strong` at 30% is the neutral the product owner approved: it
 * lands on the same grey over the light canvas and on its lighter counterpart over the
 * dark one, instead of a near white that disappears.
 */
export const SEM_EXPEDIENTE_BG = 'bg-border-strong/30';

/** What the stripe of a day with no work answers on hover. */
export function semExpedienteTexto(t: TFunction, dia: MapaDiaSemExpediente): string {
  if (dia.motivo === 'FERIADO' && dia.feriado !== null && dia.feriado !== '') {
    return t('mapa.semExpediente.feriado', { nome: dia.feriado });
  }
  if (dia.motivo === 'FIM_DE_SEMANA') {
    return t('mapa.semExpediente.fimDeSemana');
  }
  return t('mapa.semExpediente.generico');
}

export type TFarolTone = 'danger' | 'warning' | 'success' | 'neutral';

export const FAROL_TONE: Record<FarolCor, TFarolTone> = {
  VERMELHO: 'danger',
  AMBAR: 'warning',
  VERDE: 'success',
  CINZA: 'neutral',
};

export function formatDate(date: string | null | undefined): string {
  if (date === null || date === undefined || date === '') {
    return '-';
  }
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}

/** Dates as dd/mm, for the week labels and the squares, where width is scarce. */
export function formatDiaMes(date: string | null | undefined): string {
  if (date === null || date === undefined || date === '') {
    return '-';
  }
  const [, month, day] = date.split('-');
  return `${day}/${month}`;
}

export function formatCarga(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) {
    return `${m}min`;
  }
  if (m === 0) {
    return `${h}h`;
  }
  return `${h}h${m}min`;
}

/**
 * The load of a lane, in WHOLE hours with the thousands separator ("4.440"). The lane
 * header used to print `formatCarga`, which spells minutes out ("4440h3min"): three
 * minutes of precision on a four thousand hour pile is noise, and the missing separator
 * made the number unreadable at a glance. `formatCarga` stays for the tooltips, where
 * the exact minutes still earn their place.
 */
export function formatCargaHoras(minutes: number): string {
  return Math.round(minutes / 60).toLocaleString('pt-BR');
}

/** The grid carries hours, the formatter speaks minutes: one place does the conversion. */
export function formatHoras(hours: number): string {
  return formatCarga(Math.round(hours * 60));
}

/**
 * The Monday of the week an ISO date falls in. Dropping an OP always lands on a WEEK,
 * never on a weekday: the engine's queue owns the exact day, so snapping here is what
 * makes the confirmation message ("not before the week of dd/mm") literally true.
 * An unparseable date comes back untouched, and the API rejects it instead of the
 * screen writing a silent Monday of its own.
 */
export function inicioDaSemana(date: string): string {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return date;
  }
  const diasAteSegunda = (parsed.getUTCDay() + 6) % DIAS_SEMANA;
  const segunda = new Date(parsed.getTime() - diasAteSegunda * MS_POR_DIA);
  return segunda.toISOString().slice(0, 10);
}

/** Cells outside sewing (preparacao, finalizacao) start hidden on the timeline. */
export function isCelulaCostura(tipo: CelulaTipo): boolean {
  return tipo === 'montagem';
}

/**
 * The colour of a brand on the map. It is the brand's OWN colour, chosen in the registry
 * (Cadastros > Clientes e marcas) or suggested by the API while nobody chose, so the same
 * brand keeps the same colour across the two subtabs, across reloads and across filters.
 * The map never derives a colour from a position in a list.
 */
export type MarcaCor = IClassesCorMarca;

/** The brands in view, indexed by name: the bands and the chips read the colour from here. */
export function corPorMarca(marcas: Array<MapaMarca>): Map<string, MarcaCor> {
  return new Map(marcas.map((item) => [item.marca, classesDaCorMarca(item.cor)]));
}

export function corDaMarca(marca: string, cores: Map<string, MarcaCor>): MarcaCor {
  return cores.get(marca) ?? CLASSES_SEM_COR;
}

/**
 * How many brands keep their name out of the band. With twelve colours some pairs still
 * look alike to a colour-blind reader, so every brand outside the biggest ones writes its
 * name before the OP number: the colour ranks, the word identifies.
 */
export const MARCAS_SO_COR = 4;

/** Brands that can go by colour alone: the biggest ones, which the legend lists first. */
export function marcasSoCor(marcas: Array<MapaMarca>): Set<string> {
  return new Set(marcas.slice(0, MARCAS_SO_COR).map((item) => item.marca));
}

/**
 * The share of the pieces in the window a brand holds. Below 1% it reads as "<1%" instead
 * of rounding to zero: a brand on screen never weighs nothing.
 */
export function formatPctPecas(pecas: number, total: number): string {
  if (total <= 0) {
    return '0%';
  }
  const pct = (pecas / total) * 100;
  if (pct > 0 && pct < 1) {
    return '<1%';
  }
  return `${Math.round(pct)}%`;
}
